import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Scaffold } from "../Scaffold";
import {
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  AlertColor,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LoadingSpinner from "../LoadingSpinner";
import { State } from "../redux/types";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { rsAdminUpdate, rsAdmin } from "../api/api";
import { setRsAdminResult, setResult, setAdvanceResult, setSelect } from "../redux/actions";
import AppSnackbar from "../components/AppSnackbar";
import ConfirmActionDialog from "../components/ConfirmActionDialog";

type StoreDetails = {
  storeId: string;
  /** Lookup key for GET /rsadmin/store/{storeNumber} (e.g. zaxlab) */
  storeNumber?: string;
  merchantId: string;
  terminalId?: string;
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  [key: string]: any;
};

export const DashboardRSadmin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { role } = useAuth();
  const setLogin = useSelector((state: State) => state.login);
  const token = useSelector((state: State) => state.login?.idToken);
  const rsAdminResult = useSelector((state: State) => state.rsAdminResult);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<StoreDetails | null>(null);
  const [editMerchantId, setEditMerchantId] = useState("");
  const [editTerminalId, setEditTerminalId] = useState("");

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");
  const [isSaving, setIsSaving] = useState(false);

  const showSnackbar = (message: string, severity: AlertColor = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChange = () => {
    dispatch(setRsAdminResult(null as any));
    dispatch(setResult(null as any));
    dispatch(setAdvanceResult(null as any));
    dispatch(setSelect(null));
    navigate("/dashboard");
  };

  const handleEditClick = (storeDetails: StoreDetails) => {
    setEditingStore(storeDetails);
    setEditMerchantId(storeDetails.merchantId || "");
    setEditTerminalId(storeDetails.terminalId || "");
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingStore(null);
  };

  const performEditSave = async () => {
    if (!editingStore) {return;}

    setIsSaving(true);
    try {
      const updateBody = {
        storeId: editingStore.storeId,
        freedomPayStoreId: editMerchantId,
        terminalId: editTerminalId,
      };

      await rsAdminUpdate(updateBody);

      const storeLookupKey =
        editingStore.storeNumber?.trim() || editingStore.storeId;
      const refreshedData = await rsAdmin(storeLookupKey, token);
      dispatch(setRsAdminResult([refreshedData]));

      setConfirmOpen(false);
      handleEditClose();
      showSnackbar("Store updated successfully!", "success");
    } catch (err: any) {
      setConfirmOpen(false);
      showSnackbar(err.message || "Failed to update store", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const storeDetailsFromItem = (item: unknown): StoreDetails => {
    const anyItem = item as { data?: { storeDetails?: StoreDetails }; storeDetails?: StoreDetails };
    const body = anyItem?.data ?? anyItem;
    return body?.storeDetails ?? ({} as StoreDetails);
  };

  const getStoreDataList = (): { storeDetails: StoreDetails; index: number }[] => {
    if (!rsAdminResult) {return [];}

    if (Array.isArray(rsAdminResult)) {
      return rsAdminResult.map((item, index) => ({
        storeDetails: storeDetailsFromItem(item),
        index,
      }));
    }

    const details = storeDetailsFromItem(rsAdminResult);
    if (details && Object.keys(details).length > 0) {
      return [{ storeDetails: details, index: 0 }];
    }

    return [];
  };

  const storeDataList = getStoreDataList();

  return (
    <Scaffold navBar rsAdmin>
      {isSaving && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            bgcolor: "rgba(255,255,255,0.7)",
          }}
        >
          <LoadingSpinner />
        </Box>
      )}
      {/* Left aligned section */}
      <Box sx={{ textAlign: "left", mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Welcome {setLogin?.firstName},
        </Typography>

        {role === "medley-admin-group" && (
          <Button color="primary" variant="text" onClick={handleChange}>
            Switch to Guest Experience
          </Button>
        )}
      </Box>

      {/* Center aligned table section */}
      {storeDataList.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Store Results
          </Typography>
          <TableContainer component={Paper} sx={{ maxWidth: "fit-content" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell><strong>Store ID</strong></TableCell>
                  <TableCell><strong>Merchant ID</strong></TableCell>
                  <TableCell><strong>Terminal ID</strong></TableCell>
                  <TableCell><strong>Store Name</strong></TableCell>
                  <TableCell><strong>Address</strong></TableCell>
                  <TableCell><strong>City</strong></TableCell>
                  <TableCell><strong>State</strong></TableCell>
                  <TableCell><strong>Postal Code</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {storeDataList.map(({ storeDetails, index }) => (
                  <TableRow key={storeDetails.storeId || index}>
                    <TableCell>{storeDetails.storeId || "-"}</TableCell>
                    <TableCell>{storeDetails.merchantId || "-"}</TableCell>
                    <TableCell>{storeDetails.terminalId || "-"}</TableCell>
                    <TableCell>{storeDetails.name || "-"}</TableCell>
                    <TableCell>{storeDetails.address || "-"}</TableCell>
                    <TableCell>{storeDetails.city || "-"}</TableCell>
                    <TableCell>{storeDetails.state || "-"}</TableCell>
                    <TableCell>{storeDetails.postalCode || "-"}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditClick(storeDetails)}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Store Information</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {/* Read-only Fields */}
            <TextField
              label="Store ID"
              value={editingStore?.storeId || ""}
              fullWidth
              disabled
              slotProps={{ input: { readOnly: true } }}
            />

            {/* Editable Fields */}
            <TextField
              label="Terminal ID"
              value={editTerminalId}
              onChange={(e) => setEditTerminalId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Merchant ID"
              value={editMerchantId}
              onChange={(e) => setEditMerchantId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Store Name"
              value={editingStore?.name || ""}
              fullWidth
              disabled
              slotProps={{ input: { readOnly: true } }}
            />
            <TextField
              label="Address"
              value={editingStore?.address || ""}
              fullWidth
              disabled
              slotProps={{ input: { readOnly: true } }}
            />
            <TextField
              label="City"
              value={editingStore?.city || ""}
              fullWidth
              disabled
              slotProps={{ input: { readOnly: true } }}
            />
            <TextField
              label="State"
              value={editingStore?.state || ""}
              fullWidth
              disabled
              slotProps={{ input: { readOnly: true } }}
            />
            <TextField
              label="Postal Code"
              value={editingStore?.postalCode || ""}
              fullWidth
              disabled
              slotProps={{ input: { readOnly: true } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleEditClose}
            variant="outlined"
            sx={{
              color: "#041E42",
              borderColor: "#041E42",
              "&:hover": {
                borderColor: "#0a3d7a",
                backgroundColor: "rgba(4, 30, 66, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => setConfirmOpen(true)}
            variant="contained"
            color="primary"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialog before applying the edit */}
      <ConfirmActionDialog
        open={confirmOpen}
        actionLabel="Are you sure you wish to edit this store?"
        description=""
        loading={isSaving}
        onConfirm={performEditSave}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* Snackbar for notifications */}
      <AppSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Scaffold>
  );
};
